import { useMemo } from "react";
import type { Band, BandSlotResolved, Song, StagePreset, SlotRole } from "../../infra/api.js";

// =========================================================================
// SongCover — small pure-SVG cover-art used as the left thumbnail in the
// library list. Mirrors the band's stage preset palette so the gallery
// feels of a piece with /bands and /bands/:id.
//
// Originally a 1084-line procedural album-art generator with PNG export
// (kept under stash). For F7 we only need a recognizable thumbnail; we
// dropped the PNG/serializeForExport path and the multi-tier layout.
// =========================================================================

export interface SongCoverProps {
  song: Song;
  band: Band | null;
  size?: number;
}

const SLOT_PRIORITY: SlotRole[] = ["vocal", "guitar", "bass", "keys", "drums"];

function pickFeatureSlot(slots: BandSlotResolved[]): BandSlotResolved | null {
  const byRole = new Map(slots.map((s) => [s.slot_role, s] as const));
  for (const role of SLOT_PRIORITY) {
    const s = byRole.get(role);
    if (s) return s;
  }
  return null;
}

function catalogOf(projectId: string): string {
  const last = projectId
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(-6)
    .toUpperCase();
  return `MS·${last || "000000"}`;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

interface PaletteStop {
  offset: number;
  color: string;
  opacity?: number;
}

interface PresetPalette {
  back: { stops: PaletteStop[] };
  glow?: { cx: number; cy: number; rx: number; ry: number; stops: PaletteStop[] };
  floor: string;
  ink: string;
  accent: string;
  monoInk: string;
}

const PALETTES: Record<StagePreset, PresetPalette> = {
  underground: {
    back: {
      stops: [
        { offset: 0, color: "#1a120c" },
        { offset: 0.5, color: "#2a1a10" },
        { offset: 1, color: "#150f08" },
      ],
    },
    glow: {
      cx: 0.5,
      cy: 0.9,
      rx: 0.65,
      ry: 0.6,
      stops: [
        { offset: 0, color: "#e8443a", opacity: 0.35 },
        { offset: 1, color: "#e8443a", opacity: 0 },
      ],
    },
    floor: "#0a0604",
    ink: "#f4ebd9",
    accent: "#e8443a",
    monoInk: "#d6c8a8",
  },
  jazz: {
    back: {
      stops: [
        { offset: 0, color: "#0a0a2a" },
        { offset: 0.55, color: "#1a1a4a" },
        { offset: 1, color: "#050518" },
      ],
    },
    glow: {
      cx: 0.5,
      cy: 1.0,
      rx: 0.7,
      ry: 0.5,
      stops: [
        { offset: 0, color: "#503ca0", opacity: 0.32 },
        { offset: 1, color: "#503ca0", opacity: 0 },
      ],
    },
    floor: "#06030f",
    ink: "#f4ebd9",
    accent: "#f0c84a",
    monoInk: "#c8c0e0",
  },
  openair: {
    back: {
      stops: [
        { offset: 0, color: "#2a1a5a" },
        { offset: 0.35, color: "#6b3a78" },
        { offset: 0.7, color: "#d86a3a" },
        { offset: 0.95, color: "#f0b864" },
        { offset: 1, color: "#5a2818" },
      ],
    },
    floor: "#1a0e08",
    ink: "#1a0e08",
    accent: "#e8443a",
    monoInk: "#3a1808",
  },
  garage: {
    back: {
      stops: [
        { offset: 0, color: "#2a2a30" },
        { offset: 1, color: "#3a3a45" },
      ],
    },
    floor: "#2a2a30",
    ink: "#f4ebd9",
    accent: "#f0c84a",
    monoInk: "#b8b8c0",
  },
  synthwave: {
    back: {
      stops: [
        { offset: 0, color: "#0a0028" },
        { offset: 0.4, color: "#2a0a4a" },
        { offset: 0.8, color: "#1a0530" },
        { offset: 1, color: "#050015" },
      ],
    },
    glow: {
      cx: 0.5,
      cy: 1.0,
      rx: 1.0,
      ry: 0.6,
      stops: [
        { offset: 0, color: "#f050c8", opacity: 0.42 },
        { offset: 1, color: "#f050c8", opacity: 0 },
      ],
    },
    floor: "#050015",
    ink: "#ffeaff",
    accent: "#f050c8",
    monoInk: "#e0a8e8",
  },
  acoustic: {
    back: {
      stops: [
        { offset: 0, color: "#4a2818" },
        { offset: 0.4, color: "#6a3a20" },
        { offset: 0.7, color: "#8a4a28" },
        { offset: 1, color: "#2a1408" },
      ],
    },
    glow: {
      cx: 0.5,
      cy: 0.2,
      rx: 0.6,
      ry: 0.4,
      stops: [
        { offset: 0, color: "#f0c84a", opacity: 0.22 },
        { offset: 1, color: "#f0c84a", opacity: 0 },
      ],
    },
    floor: "#2a1408",
    ink: "#f4ebd9",
    accent: "#f0c84a",
    monoInk: "#d8b888",
  },
};

function paletteFor(band: Band | null): PresetPalette {
  if (!band) return PALETTES.underground;
  return PALETTES[band.stage_config.preset];
}

function truncateOneLine(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

export function SongCover({ song, band, size = 96 }: SongCoverProps): JSX.Element {
  const palette = paletteFor(band);
  const cat = catalogOf(song.project_id);
  const title = (song.title ?? "untitled").trim() || "untitled";
  const slotInitial = useMemo(() => {
    if (!band) return "·";
    const featured = pickFeatureSlot(band.slots);
    return featured ? featured.slot_role.charAt(0).toUpperCase() : "·";
  }, [band]);

  // Stable id prefix for gradient defs (avoid collisions in a list).
  const idPrefix = useMemo(() => `sc-${slug(song.project_id || "x") || "x"}`, [song.project_id]);

  const VB = 100;

  return (
    <div
      className="relative inline-block overflow-hidden border border-border bg-tape"
      style={{ width: size, height: size, lineHeight: 0 }}
      role="img"
      aria-label={`${title} — ${band?.name ?? "no band"} cover`}
    >
      <svg
        viewBox={`0 0 ${VB} ${VB}`}
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={`${idPrefix}-bg`} x1="0" y1="0" x2="0" y2="1">
            {palette.back.stops.map((s, i) => (
              <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity ?? 1} />
            ))}
          </linearGradient>
          {palette.glow && (
            <radialGradient
              id={`${idPrefix}-glow`}
              cx={palette.glow.cx}
              cy={palette.glow.cy}
              r={Math.max(palette.glow.rx, palette.glow.ry)}
              fx={palette.glow.cx}
              fy={palette.glow.cy}
            >
              {palette.glow.stops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity ?? 1} />
              ))}
            </radialGradient>
          )}
          <pattern
            id={`${idPrefix}-scan`}
            x="0"
            y="0"
            width="2"
            height="2"
            patternUnits="userSpaceOnUse"
          >
            <rect width="2" height="1" fill="rgba(0,0,0,0)" />
            <rect y="1" width="2" height="1" fill="rgba(0,0,0,0.18)" />
          </pattern>
        </defs>

        <rect width={VB} height={VB} fill={`url(#${idPrefix}-bg)`} />
        {palette.glow && <rect width={VB} height={VB} fill={`url(#${idPrefix}-glow)`} />}
        <rect width={VB} height={VB} fill={`url(#${idPrefix}-scan)`} opacity={0.45} />

        {/* Floor strip */}
        <rect x={0} y={84} width={VB} height={16} fill={palette.floor} />
        <rect x={0} y={84} width={VB} height={0.5} fill={palette.accent} opacity={0.5} />

        {/* Catalog (top-right) */}
        <text
          x={VB - 4}
          y={9}
          textAnchor="end"
          fontFamily='"JetBrains Mono", "Courier New", monospace'
          fontSize={4.2}
          fill={palette.monoInk}
          letterSpacing="0.18em"
        >
          {cat}
        </text>

        {/* Slot-role initial (center, big) */}
        <text
          x={VB / 2}
          y={62}
          textAnchor="middle"
          fontFamily='"Fraunces", Georgia, "Times New Roman", serif'
          fontSize={38}
          fontWeight={700}
          fontStyle="italic"
          fill={palette.ink}
          opacity={0.92}
        >
          {slotInitial}
        </text>

        {/* Title strip (bottom) */}
        <text
          x={4}
          y={94}
          fontFamily='"Fraunces", Georgia, "Times New Roman", serif'
          fontSize={7}
          fontWeight={600}
          fill={palette.ink}
          style={{ letterSpacing: "-0.01em" }}
        >
          {truncateOneLine(title, 14)}
        </text>
      </svg>
    </div>
  );
}

export default SongCover;
